// Guild panel — tier progression bar, unlocked rooms, member list, quick
// actions (enter hall / leave hall / open bank). Opens via /guild UI.

import type { ClientStore } from "../state.js";
import type { Net } from "../net.js";
import { openPanel } from "./shared_panel.js";

export function openGuildPanel(store: ClientStore, net: Net): void {
  net.send({ t: "guild_info" });
  openPanel({
    id: "panel-guild",
    title: "Guild",
    store,
    render(state) {
      const g = state.guild;
      if (!g) {
        return `
          <style>.panel-block { background:var(--panel,#121520); border:1px solid var(--line,#2a2f3d); border-radius:6px; padding:14px; }
          .panel-block button { font:inherit; padding:6px 14px; cursor:pointer; }</style>
          <div class="panel-block">
            <p>You are not in a guild.</p>
            <div style="margin-top:12px;">
              <div class="panel-section-title">Found a Guild</div>
              <div style="display:flex;gap:6px;align-items:center;">
                <input id="guild-name" type="text" placeholder="Guild name" style="flex:1;" />
                <button id="btn-found-guild">Found Guild</button>
              </div>
              <p class="muted" style="margin-top:6px;font-size:11px;">Requires Patron tier. You will be the guild founder and leader.</p>
            </div>
          </div>`;
      }
      const p = g.progress;
      const hasProgress = !!p;
      const bar = (hasProgress && p!.next_tier)
        ? progressBar(p!.xp, p!.next_tier.required_xp, p!.current_members, p!.next_tier.required_members)
        : hasProgress ? `<div class="muted">Maxed tier.</div>` : "";
      const rooms = hasProgress
        ? p!.unlocked_rooms.map((r) => `<span class="room-pill">${escape(roomDisplay(r))}</span>`).join(" ")
        : "";
      const memberRows = g.members.length
        ? g.members.map((m) => `<tr>
            <td>${escape(m.name)}</td>
            <td><span class="rank-${escape(m.rank)}">${escape(m.rank)}</span></td>
          </tr>`).join("")
        : `<tr><td colspan="2" class="muted">No members recorded.</td></tr>`;
      return `
        <style>
          .panel-block { background:var(--panel,#121520); border:1px solid var(--line,#2a2f3d); border-radius:6px; padding:12px 14px; margin-bottom:12px; }
          .panel-block h3 { color:var(--gold,#d9b150); font-size:13px; margin:0 0 8px; text-transform:uppercase; letter-spacing:0.08em; }
          .panel-block .grid-form { display:grid; grid-template-columns: 140px 1fr; gap:4px 12px; font-size:12px; }
          .panel-block table { width:100%; border-collapse:collapse; font-size:12px; margin-top:4px; }
          .panel-block td, .panel-block th { padding:4px 6px; border-bottom:1px solid var(--line,#2a2f3d); text-align:left; }
          .bar { height:10px; border:1px solid var(--line,#2a2f3d); border-radius:4px; overflow:hidden; background:#0c0e14; margin: 4px 0 2px; }
          .bar-fill { height:100%; background: linear-gradient(90deg, #d9b150, #e8c878); }
          .bar-label { font-size:10px; color:var(--dim,#7c8194); }
          .room-pill { display:inline-block; padding:2px 10px; margin:2px; background:#1a2010; border:1px solid #5fb878; color:#5fb878; border-radius:12px; font-size:11px; }
          .room-pill.locked { background:#24252c; border-color:var(--line,#2a2f3d); color:var(--dim,#7c8194); }
          .rank-founder { color:#e8c878; font-weight:600; }
          .rank-officer { color:#8ab8e8; }
          .rank-member { color:var(--dim,#7c8194); }
          .actions-row { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }
          .actions-row button { font:inherit; font-size:12px; padding:4px 10px; }
        </style>
        <div class="panel-block">
          <h3>${escape(g.name)}</h3>
          <div class="grid-form">
            <label>Your rank</label><div><span class="rank-${escape(g.rank)}">${escape(g.rank)}</span></div>
            <label>Members</label><div class="muted">${g.members.length}</div>
            ${hasProgress ? `
              <label>Tier</label><div><strong>${p!.tier}</strong> — ${escape(p!.tier_name)}</div>
              <label>Bank tabs</label><div class="muted">${p!.bank_tabs}</div>
              <label>Total XP</label><div class="muted">${p!.xp.toLocaleString()}</div>
            ` : ""}
          </div>
          <div class="actions-row">
            <button id="btn-hall">Enter Hall</button>
            <button id="btn-leave-hall">Leave Hall</button>
            <button id="btn-bank">Open Bank</button>
            <button id="btn-leave" class="danger">Leave Guild</button>
          </div>
        </div>
        ${hasProgress ? `
          <div class="panel-block">
            <h3>Progression</h3>
            ${bar}
            <div style="margin-top:8px;">
              <strong>Unlocked rooms:</strong> ${rooms}
            </div>
          </div>` : ""}
        <div class="panel-block">
          <h3>Members (${g.members.length})</h3>
          <table><thead><tr><th>Name</th><th>Rank</th></tr></thead><tbody>${memberRows}</tbody></table>
        </div>`;
    },
    bind(root) {
      root.querySelector<HTMLButtonElement>("#btn-found-guild")?.addEventListener("click", () => {
        const name = root.querySelector<HTMLInputElement>("#guild-name")?.value?.trim();
        if (!name) return;
        net.send({ t: "guild_found", name });
      });
      root.querySelector<HTMLButtonElement>("#btn-hall")?.addEventListener("click", () => net.send({ t: "guild_hall_enter" }));
      root.querySelector<HTMLButtonElement>("#btn-leave-hall")?.addEventListener("click", () => net.send({ t: "guild_hall_leave" }));
      root.querySelector<HTMLButtonElement>("#btn-bank")?.addEventListener("click", () => net.send({ t: "guild_bank_list" }));
      root.querySelector<HTMLButtonElement>("#btn-leave")?.addEventListener("click", () => {
        if (!confirm("Leave your guild? Your rank and guild-bank access will be revoked.")) return;
        net.send({ t: "guild_leave" });
      });
    },
  });
}

function roomDisplay(id: string): string {
  return {
    hall: "Hall",
    vault: "Vault",
    library: "Library",
    training_yard: "Training Yard",
  }[id] ?? id;
}

function progressBar(xp: number, xpTarget: number, members: number, memberTarget: number): string {
  const xpPct = xpTarget > 0 ? Math.min(100, (xp / xpTarget) * 100) : 100;
  const memPct = memberTarget > 0 ? Math.min(100, (members / memberTarget) * 100) : 100;
  return `
    <div class="bar-label">XP: ${xp.toLocaleString()} / ${xpTarget.toLocaleString()}</div>
    <div class="bar"><div class="bar-fill" style="width:${xpPct}%;"></div></div>
    <div class="bar-label">Members: ${members} / ${memberTarget}</div>
    <div class="bar"><div class="bar-fill" style="width:${memPct}%;"></div></div>`;
}

function escape(s: string): string {
  return (s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
